<template>
  <div class="freight-valor-saida">
    <div class="freight-valor-saida__header">
      <span class="freight-valor-saida__title">Valor de saída do veículo</span>
      <div class="freight-valor-saida__header-actions">
        <a-switch
          :checked="ativa"
          aria-label="Ativar valor de saída do veículo"
          @change="onToggleAtiva"
        />
        <button
          type="button"
          class="freight-valor-saida__chevron"
          :aria-label="
            expanded ? 'Recolher valor de saída' : 'Expandir valor de saída'
          "
          @click="expanded = !expanded"
        >
          <a-icon :type="expanded ? 'up' : 'down'" />
        </button>
      </div>
    </div>

    <div v-if="ativa && expanded" class="freight-valor-saida__body">
      <div class="freight-valor-saida__head">
        <span class="freight-valor-saida__head-cell">
          <span class="freight-valor-saida__required">*</span> Valor
        </span>
        <span class="freight-valor-saida__head-cell">
          <span class="freight-valor-saida__required">*</span> Veículo
        </span>
        <span
          class="freight-valor-saida__head-cell freight-valor-saida__head-cell--acoes"
        />
      </div>

      <div
        v-for="(linha, index) in rows"
        :key="index"
        class="freight-valor-saida__row"
      >
        <a-input
          :value="formatCurrency(linha.vlr)"
          placeholder="R$ 0,00"
          @change="(e) => onVlrChange(index, e)"
        />

        <FreightTableVehicleSelect
          :value="vehiclesOf(linha)"
          :options="veiculos"
          @change="(ids) => onVeiculoChange(index, ids)"
        />

        <span class="freight-valor-saida__acoes">
          <a-button
            v-if="index === rows.length - 1"
            class="freight-valor-saida__add"
            aria-label="Adicionar linha de valor de saída"
            @click="addRow"
          >
            <a-icon type="plus" />
          </a-button>

          <a-button
            v-if="rows.length > 1"
            type="link"
            class="freight-valor-saida__trash"
            aria-label="Remover linha de valor de saída"
            @click="removeRow(index)"
          >
            <a-icon type="delete" />
          </a-button>
        </span>
      </div>

      <p v-if="lineError" class="freight-valor-saida__error">
        {{ lineError }}
      </p>
    </div>
  </div>
</template>

<script>
import FreightTableVehicleSelect from "./FreightTableVehicleSelect.vue";
import {
  createEmptyValorSaidaLine,
  validateValoresSaida,
  valorSaidaLineVehicles,
} from "../../services/freightTableConfigForm";

export default {
  name: "FreightTableValorSaidaVeiculo",
  components: {
    FreightTableVehicleSelect,
  },
  props: {
    ativa: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Array,
      default: () => [],
    },
    veiculos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      expanded: true,
    };
  },
  computed: {
    rows() {
      return this.value && this.value.length
        ? this.value
        : [createEmptyValorSaidaLine()];
    },
    lineError() {
      const erros = validateValoresSaida({
        saidaVeiculoAtiva: this.ativa,
        valoresSaida: this.rows,
      });
      return erros.length ? erros[0].message : "";
    },
  },
  methods: {
    vehiclesOf: valorSaidaLineVehicles,
    formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "";
      const num = Number(value);
      if (!Number.isFinite(num)) return "";
      return num.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    readDigits(e) {
      const raw = String((e && e.target ? e.target.value : e) || "");
      const digits = raw.replace(/\D/g, "");
      return digits === "" ? null : Number(digits) / 100;
    },
    onToggleAtiva(checked) {
      this.$emit("update:ativa", checked);
      if (checked) {
        this.expanded = true;
        if (!this.value.length) {
          this.$emit("update:value", [createEmptyValorSaidaLine()]);
        }
      }
    },
    onVlrChange(index, e) {
      this.patchRow(index, { vlr: this.readDigits(e) });
    },
    onVeiculoChange(index, ids) {
      this.patchRow(index, {
        tp_rodado: valorSaidaLineVehicles({ tp_rodado: ids }),
      });
    },
    patchRow(index, patch) {
      const next = this.rows.map((linha, i) =>
        i === index ? { ...linha, ...patch } : { ...linha },
      );
      this.$emit("update:value", next);
    },
    addRow() {
      this.$emit("update:value", this.rows.concat([createEmptyValorSaidaLine()]));
    },
    removeRow(index) {
      if (this.rows.length <= 1) return;
      this.$emit(
        "update:value",
        this.rows.filter((_, i) => i !== index),
      );
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

@freight-valor-saida-columns: 1fr 1fr 72px;

.freight-valor-saida {
  margin: 0;
  padding: 12px 14px;
  background: #f5f5f5;
  border: 1px solid #ebebeb;
  border-radius: 8px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__header-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgba(0, 0, 0, 0.45);
    cursor: pointer;

    &:hover {
      color: rgba(0, 0, 0, 0.75);
    }
  }

  &__required {
    color: @color-danger;
    margin-right: 2px;
  }

  &__body {
    margin-top: 12px;
  }

  &__head,
  &__row {
    display: grid;
    grid-template-columns: @freight-valor-saida-columns;
    gap: 8px;
    align-items: center;
  }

  &__head {
    margin-bottom: 4px;
  }

  &__head-cell {
    font-size: 12px;
    color: @color-text-light;

    &--acoes {
      text-align: center;
    }
  }

  &__row {
    margin-bottom: 8px;
  }

  &__acoes {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  &__add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 6px;
    color: @color-primary;
  }

  &__trash {
    color: @color-danger;
    padding: 0;
  }

  &__error {
    margin: 0;
    font-size: 12px;
    color: @color-danger;
  }
}
</style>
